import React, { useCallback, useRef, useState } from "react"

import { paymentClient, useMutation } from "@trueskin-web/apis"
import { i18nLocale } from "@trueskin-web/locales"

import { usePaymentContext } from "../../payment.context"

const klarnaPayType = "pay_later"

const klarnaErrorTypes = {
  InvalidClientTokenError: "AUTHENTICATION_REQUIRED",
  InvalidContainerSelectorError: "ISSUER_UNAVAILABLE",
}

export const useKlarna = ({ paymentMethodType, order }) => {
  const mountRef = useRef()
  const { amount, currency, orderId, customerId, v2CustomerId, accountId } =
    order
  const { handlePurchase, onError } = usePaymentContext()

  const handleError = useCallback(
    ({ name = "", code = "", data: { refusalReasonCode = "" } = {} } = {}) => {
      setError(klarnaErrorTypes[name] || refusalReasonCode || code)

      onError?.({ paymentMethodType, order, refusalReasonCode })
    },
    [onError, paymentMethodType, order]
  )

  const {
    mutateAsync: createPaymentSession,
    isLoading: isCreatePaymentSessionLoading,
  } = useMutation(
    () => paymentClient.createPaymentSession("klarna", { orderId }),
    {
      onSuccess: ({ data: { clientToken } }) => {
        return new Promise((resolve) => {
          try {
            window.Klarna.Payments.init({
              client_token: clientToken,
            }).load(
              {
                container: mountRef.current,
                payment_method_category: klarnaPayType,
              },
              undefined,
              resolve
            )
          } catch (err) {
            handleError(err)
            resolve()
          }
        })
      },
      onError: handleError,
    }
  )

  const [error, setError] = useState()
  const [isProcessing, setIsProcessing] = useState(false)

  const handleSubmit = useCallback(async () => {
    try {
      setIsProcessing(true)

      window.Klarna.Payments.authorize(
        {
          payment_method_category: klarnaPayType,
        },
        {},
        ({ authorization_token, approved, show_form, error: err }) => {
          if (!approved) {
            if (!show_form) {
              handleError(err)
            }

            setIsProcessing(false)

            return
          }

          return handlePurchase({
            provider: "klarna",
            currency,
            locale: i18nLocale(),
            amount,
            authorizationToken: authorization_token,
            paymentMethod: klarnaPayType,
            customerId,
            v2CustomerId,
            accountId,
          })
            .catch(handleError)
            .finally(() => {
              setIsProcessing(false)
            })
        }
      )
    } catch (err) {
      handleError(err)

      setIsProcessing(false)
    }
  }, [
    handlePurchase,
    handleError,
    amount,
    currency,
    customerId,
    v2CustomerId,
    accountId,
  ])

  return React.useMemo(
    () => ({
      mountRef,
      handleSubmit,
      createPaymentSession,
      isCreatePaymentSessionLoading,
      isProcessing,
      error,
    }),
    [
      mountRef,
      handleSubmit,
      createPaymentSession,
      isCreatePaymentSessionLoading,
      isProcessing,
      error,
    ]
  )
}
